import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  DocumentChartBarIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  TrashIcon,
  BoltIcon,
  FunnelIcon,
  PaperAirplaneIcon,
  ClockIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { reportsAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import PositionSummary from '../components/HR/PositionSummary';
import FollowupActivity from '../components/HR/FollowupActivity';

const REPORT_VIEWS = [
  { key: 'analytics', label: 'Analytics Reports' },
  { key: 'summary', label: 'Position Summary' },
  { key: 'activity', label: 'Follow-up Activity' },
];

const today = new Date().toISOString().slice(0, 10);
const lastMonday = (() => {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
})();

export default function HRReports() {
  const { departmentId } = useParams();
  const deptId = parseInt(departmentId, 10);
  const { isAdmin, canModule } = useAuth();
  const canGenerate = canModule('hr_reports', 'add');
  const [view, setView] = useState('analytics');

  const [reports, setReports]       = useState([]);
  const [settings, setSettings]     = useState(null);
  const [positions, setPositions]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [generating, setGenerating] = useState(false);
  const [triggeringWeekly, setTriggeringWeekly] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);

  // Filter / generate form state
  const [dateFrom, setDateFrom]         = useState(lastMonday);
  const [dateTo, setDateTo]             = useState(today);
  const [posFilter, setPosFilter]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, sRes, pRes] = await Promise.all([
        reportsAPI.list(deptId),
        reportsAPI.getSettings(deptId),
        reportsAPI.listPositions(deptId),
      ]);
      setReports(rRes.data);
      setSettings(sRes.data);
      setPositions(pRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [deptId]);

  useEffect(() => { reload(); }, [reload]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (dateTo < dateFrom) { alert('End date must be after start date'); return; }
    setGenerating(true);
    try {
      await reportsAPI.generate({
        department_id: deptId,
        date_from: dateFrom,
        date_to: dateTo,
        position_filter: posFilter || null,
        status_filter: statusFilter || null,
      });
      reload();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (report) => {
    if (!window.confirm(`Delete report for ${fmtDate(report.date_from)} – ${fmtDate(report.date_to)}?`)) return;
    try {
      await reportsAPI.delete(report.id);
      setReports((prev) => prev.filter((r) => r.id !== report.id));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete report');
    }
  };

  const handleTriggerWeekly = async () => {
    if (!window.confirm('Trigger the weekly report for all HR departments now?')) return;
    setTriggeringWeekly(true);
    try {
      await reportsAPI.triggerWeekly();
      alert('Weekly report job started. Refresh in a few seconds.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to trigger weekly report');
    } finally {
      setTriggeringWeekly(false);
    }
  };

  const handleSendNow = async () => {
    if (!settings?.recipient_emails?.trim()) {
      alert('No recipient emails configured. Open Settings and add recipients first.');
      return;
    }
    if (!window.confirm('Generate last week\'s report and email it to configured recipients now?')) return;
    setSendingNow(true);
    try {
      await reportsAPI.sendNow(deptId);
      alert('Report is being generated and emailed. Check your inbox shortly.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send report');
    } finally {
      setSendingNow(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
    </div>
  );

  // Every value of candidates.status, so a status the report now counts can
  // also be filtered on. The three that were missing are not obscure ones:
  // screened_pending_review is the LARGEST status in the database (162 of 326),
  // unreadable_cv is 39, and no_response is written by the follow-up close.
  // A report that counts a status the filter cannot select is a report you
  // cannot drill into.
  //
  // Hardcoded, like the list it replaces, because the enum is not exposed over
  // the API. That is a real weakness: the next status added to
  // models/candidate.py will be missing here again, exactly as these three
  // were. Kept in the same order as the enum so a diff against the model is
  // readable by eye.
  const STATUS_OPTIONS = [
    'pending', 'screening', 'passed', 'failed',
    'interview_invited', 'details_requested', 'screened_pending_review',
    'unreadable_cv', 'no_response', 'rejected', 'form_sent', 'form_submitted',
    'schedule_sent', 'interview_scheduled', 'interviewed',
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DocumentChartBarIcon className="h-7 w-7 text-primary-600" />
            HR Reports
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Recruitment summaries, follow-up activity and downloadable reports for this department.
          </p>
        </div>
        {view === 'analytics' && <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <button
              onClick={handleTriggerWeekly}
              disabled={triggeringWeekly}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <BoltIcon className="h-4 w-4 text-amber-500" />
              {triggeringWeekly ? 'Running…' : 'Run Weekly Now'}
            </button>
          )}
          <button
            onClick={handleSendNow}
            disabled={sendingNow}
            className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            <PaperAirplaneIcon className="h-4 w-4" />
            {sendingNow ? 'Sending…' : 'Send Now'}
          </button>
        </div>}
      </div>

      <nav aria-label="HR report views" className="flex gap-1 overflow-x-auto border-b border-gray-200">
        {REPORT_VIEWS.map(({ key, label }) => (
          <button key={key} type="button" onClick={() => setView(key)} aria-pressed={view === key}
            className={`min-h-[44px] shrink-0 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600 ${
              view === key ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {label}
          </button>
        ))}
      </nav>

      {view === 'summary' && <PositionSummary key={deptId} deptId={deptId} positions={positions} canView={canModule('hr_reports', 'view')} />}
      {view === 'activity' && <FollowupActivity key={deptId} deptId={deptId} positions={positions} />}

      {view === 'analytics' && <>
      {/* Generate report form */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <FunnelIcon className="h-5 w-5 text-primary-600" />
          Generate Report
        </h2>
        <form onSubmit={handleGenerate} className="space-y-4">
          {/* Date range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
              <input
                type="date"
                value={dateFrom}
                max={today}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                max={today}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
          </div>

          {/* Optional filters */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Filter by position <span className="text-gray-400">(optional)</span>
              </label>
              <select
                value={posFilter}
                onChange={(e) => setPosFilter(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              >
                <option value="">All positions</option>
                {positions.map((p) => (
                  <option key={p.id} value={p.title}>{p.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Filter by status <span className="text-gray-400">(optional)</span>
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              >
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{fmtStatus(s)}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={generating || !canGenerate}
            title={canGenerate ? '' : "🔒 You don't have permission"}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <DocumentChartBarIcon className="h-4 w-4" />
            {generating ? 'Generating PDF…' : 'Generate Report'}
          </button>
          {generating && (
            <p className="text-xs text-gray-400 flex items-center gap-1">
              <ClockIcon className="h-3.5 w-3.5" />
              This may take a few seconds while the PDF is built…
            </p>
          )}
        </form>
      </div>

      {/* Report history */}
      <div>
        <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <CalendarDaysIcon className="h-5 w-5 text-primary-600" />
          Report History
          <span className="ml-auto text-xs font-normal text-gray-400">
            {reports.length} report{reports.length !== 1 ? 's' : ''}
          </span>
        </h2>

        {reports.length === 0 ? (
          <div className="text-center py-14 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <DocumentChartBarIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="font-semibold text-gray-600">No reports yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Generate your first report using the form above.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Period</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Filters</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Generated</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {reports.map((r) => (
                  <ReportRow
                    key={r.id}
                    report={r}
                    onDelete={() => handleDelete(r)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>}
    </div>
  );
}

// ── Report row ────────────────────────────────────────────────────────────────

function ReportRow({ report, onDelete }) {
  const [deleting,    setDeleting]    = useState(false);
  const [viewing,     setViewing]     = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Fetch as blob through axios (carries JWT header) then open a local object URL
  const handleView = async () => {
    setViewing(true);
    try {
      const res = await reportsAPI.viewBlob(report.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      alert('Could not load report. Please try again.');
    } finally {
      setViewing(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await reportsAPI.downloadBlob(report.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `hr_report_${report.date_from}_${report.date_to}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      alert('Could not download report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete();
    } finally {
      setDeleting(false);
    }
  };

  const filters = [
    report.position_filter && `Position: ${report.position_filter}`,
    report.status_filter   && `Status: ${fmtStatus(report.status_filter)}`,
  ].filter(Boolean);

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="font-medium text-gray-800">
          {fmtDate(report.date_from)} – {fmtDate(report.date_to)}
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          report.report_type === 'weekly'
            ? 'bg-indigo-50 text-indigo-700'
            : 'bg-gray-100 text-gray-600'
        }`}>
          {report.report_type === 'weekly' ? '⚡ Weekly Auto' : '📋 Manual'}
        </span>
      </td>
      <td className="px-4 py-3 text-gray-500 text-xs">
        {filters.length > 0 ? filters.join(' · ') : <span className="text-gray-300">None</span>}
      </td>
      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
        {fmtDateTime(report.generated_at)}
      </td>
      <td className="px-4 py-3">
        {report.has_file ? (
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleView}
              disabled={viewing}
              title="View in browser"
              className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-40"
            >
              {viewing ? <SpinIcon /> : <EyeIcon className="h-4 w-4" />}
            </button>
            <button
              onClick={handleDownload}
              disabled={downloading}
              title="Download PDF"
              className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-40"
            >
              {downloading ? <SpinIcon /> : <ArrowDownTrayIcon className="h-4 w-4" />}
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              title="Delete report"
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <span className="text-xs text-red-400">File missing</span>
        )}
      </td>
    </tr>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function SpinIcon() {
  return (
    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
  );
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return d; }
}

function fmtDateTime(dt) {
  if (!dt) return '—';
  try {
    return new Date(dt).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return dt; }
}

function fmtStatus(s) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
